import { ActionName } from 'api/endpoints/action-names';
import classNames from 'classnames';
import { Datepicker } from 'components/UIComponents/Datepicker';
import { InputComponent } from 'components/UIComponents/EntityTable';
import { useClaimResolutionTypeContext } from 'providers/ClaimResolutionTypeProvider';
import { ChangeEvent, useMemo } from 'react';
import { formatDate } from 'utils/formatDate';
import './ActionNames.css';

const keyTypeId: keyof ActionName = 'typeId';
const keySubstatusId: keyof ActionName = 'substatusId';
const keyHearingDate: keyof ActionName = 'hearingDate';

export const ActionNameInput: InputComponent<ActionName> = ({
  columnKey,
  item,
  fromTable,
}) => {
  const { className, disabled } = fromTable;

  switch (columnKey) {
    case 'name':
      return (
        <input
          type="text"
          name={columnKey}
          maxLength={50}
          required
          className={classNames(className, 'utils-clean-input')}
          disabled={disabled}
          placeholder="Enter name"
          defaultValue={item?.name}
        />
      );
    case keyTypeId:
      return (
        <ResolutionTypeInput
          columnKey={columnKey}
          fromTable={fromTable}
          item={item}
        />
      );
    case keySubstatusId:
      return (
        <ResolutionSubtypeInput
          columnKey={columnKey}
          fromTable={fromTable}
          item={item}
        />
      );
    case keyHearingDate:
      return (
        <ActionNameHearingDateInput
          columnKey={columnKey}
          fromTable={fromTable}
          item={item}
        />
      );
    default:
      return null;
  }
};

const ResolutionTypeInput: InputComponent<ActionName> = ({ item, fromTable }) => {
  const { disabled, className, getValue, setValue } = fromTable;

  const { resolutionStatusList, resolutionStatus, resolutionSubstatusList } =
    useClaimResolutionTypeContext();

  const itemLabelText = useMemo(
    () => resolutionStatus(item?.typeId)?.resolutionTypeName ?? '',
    [item?.typeId, resolutionStatus],
  );

  const selectedTypeId = getValue(keyTypeId) ?? item?.typeId;

  const handleValueChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = +event.target.value;
    const firstSubtype = resolutionSubstatusList(value)[0];

    setValue(keyTypeId, value);
    setValue(keySubstatusId, firstSubtype?.actionSubStatusId);
  };

  if (disabled) {
    return <p className="disabled-value">{itemLabelText}</p>;
  }
  return (
    <select
      name={keyTypeId}
      className={classNames(className, 'utils-clean-input')}
      required
      disabled={disabled}
      defaultValue={item?.typeId}
      value={selectedTypeId ?? ''}
      onChange={handleValueChange}
    >
      {/* placeholder */}
      <option disabled value="">
        Select resolution type
      </option>
      {resolutionStatusList.map((o) => (
        <option key={o.resolutionTypeId} value={o.resolutionTypeId}>
          {o.resolutionTypeName}
        </option>
      ))}
    </select>
  );
};

const ResolutionSubtypeInput: InputComponent<ActionName> = ({
  columnKey,
  item,
  fromTable,
}) => {
  const { disabled, className, getValue, setValue } = fromTable;
  const selectedTypeId = getValue(keyTypeId) ?? item?.typeId;
  const selectedSubTypeId = getValue(keySubstatusId) ?? item?.substatusId;

  const { resolutionSubstatusList } = useClaimResolutionTypeContext();
  const options = useMemo(
    () => resolutionSubstatusList(selectedTypeId),
    [resolutionSubstatusList, selectedTypeId],
  );

  const handleValueChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = +event.target.value;
    setValue(columnKey, value);
  };

  if (disabled) {
    return (
      <p className="disabled-value">
        {options.find((o) => o.actionSubStatusId === item?.substatusId)
          ?.actionSubStatusName ?? ''}
      </p>
    );
  }

  return (
    <select
      name={columnKey}
      className={classNames(className, 'utils-clean-input')}
      required
      disabled={disabled}
      value={selectedSubTypeId ?? ''}
      onChange={handleValueChange}
    >
      {/* placeholder */}
      <option disabled value="">
        Select substatus
      </option>
      {options.map((o) => (
        <option key={o.actionSubStatusId} value={o.actionSubStatusId}>
          {o.actionSubStatusName}
        </option>
      ))}
    </select>
  );
};

const ActionNameHearingDateInput: InputComponent<ActionName> = ({
  columnKey,
  item,
  fromTable,
}) => {
  const { disabled, className, getValue, setValue } = fromTable;

  const selectedTypeId = getValue(keyTypeId) ?? item?.typeId;
  const selectedSubTypeId = getValue(keySubstatusId) ?? item?.substatusId;
  const value = getValue(keyHearingDate) ?? item?.hearingDate;

  const { isHearingDateRequired } = useClaimResolutionTypeContext();
  const isRequiredDate = useMemo(
    () => isHearingDateRequired(selectedTypeId, selectedSubTypeId),
    [isHearingDateRequired, selectedTypeId, selectedSubTypeId],
  );

  const handleValueChange = (value: string | undefined) => {
    setValue(columnKey, value);
  };

  if (disabled) return <p className="disabled-value">{formatDate(value)}</p>;
  return (
    <Datepicker
      placeholder={item ? '' : 'Select date'}
      required={isRequiredDate && !value}
      disabled={disabled}
      formatLabel={formatDate}
      className={classNames(className, 'utils-clean-input')}
      name={columnKey}
      value={value ?? ''}
      onChange={(e) => handleValueChange(e)}
    />
  );
};
